import { useCallback, useMemo, useState } from "react";
import { auth } from "../lib/firebase";
import { authenticatedFetch } from "../lib/api";
import { ExtensionWhitelistEntry, ExtensionRequest } from "../types";
import { SuggestedExtension } from "../lib/suggestedExtensions";
import { notifyOrgPolicyChanged } from "../lib/orgPolicyBroadcast";
import { notifyDenyListChanged } from "../lib/denyListEvents";
import { DenyConflict, SubmitRequestResult, denyConflictFrom } from "../lib/extensionRequests";
import { errorWithRef, fallbackError } from "../lib/apiError";

/**
 * ENG-3353: what approving a request came to. `denyConflict` is set when it was refused because the
 * extension is on the deny list — the one refusal the console answers with a choice rather than an
 * error message.
 */
export interface ApproveRequestResult {
  success: boolean;
  error?: string;
  denyConflict?: DenyConflict;
}

export interface ApproveRequestOptions {
  /** Take the extension off the deny list and allow it, in one step. Only after the admin confirms. */
  removeFromDenyList?: boolean;
}

/** ENG-3502: how `restoreExtension` puts an entry back. */
export interface RestoreExtensionOptions {
  /**
   * Put it back only if the list still has an entry, read fresh from the server. Set by Undo: an
   * empty list comes back as `listEmpty` and nothing is written.
   */
  onlyIntoExistingList?: boolean;
}

export interface RestoreExtensionResult {
  success: boolean;
  error?: string;
  /** The list was empty when read, so nothing was written (`onlyIntoExistingList`). */
  listEmpty?: boolean;
}

// Tenant extension-whitelist data hook (ENG-2206). Mirrors the useBusinessTeam pattern:
// authenticatedFetch (Firebase bearer token, tenant resolved server-side), refetch-after-write,
// and a reset() called on logout so one tenant's data can't leak to the next user.
export function useExtensionWhitelist() {
  const [whitelist, setWhitelist] = useState<ExtensionWhitelistEntry[]>([]);
  const [requests, setRequests] = useState<ExtensionRequest[]>([]);
  const [whitelistError, setWhitelistError] = useState<string | null>(null);
  const [addingExtension, setAddingExtension] = useState(false);
  // False until the first whitelist fetch resolves, so the UI can distinguish "still loading" from
  // "empty = inherits global defaults" - otherwise a reload briefly reads as "entries gone".
  const [whitelistLoaded, setWhitelistLoaded] = useState(false);
  // ENG-3350: why the LIST failed to load, and only that. `whitelistError` is also written by every
  // write, so read as the list's load error it could name another failure.
  const [whitelistLoadError, setWhitelistLoadError] = useState<string | null>(null);
  // ENG-3528: the pending requests' own load state. The two loads run together, and a requests
  // failure used to land in `whitelistError`, which the allow list's success then cleared: the queue
  // read "No pending requests." while members were waiting. `requestsLoaded` is set only on success.
  const [requestsLoaded, setRequestsLoaded] = useState(false);
  const [requestsError, setRequestsError] = useState<string | null>(null);

  // Admin-only: the tenant's exclusive whitelist. Empty means "inherits global defaults".
  //
  // `whitelistLoaded` is set ONLY on success. It was set in a finally, which made a failed fetch
  // render as the "inherits global defaults" empty state — the opposite of what the flag exists to
  // prevent — and, because DashboardLayout gates the re-fetch on it, made that failure permanent
  // for the session. Left false, returning to Policy retries.
  const fetchWhitelist = useCallback(async () => {
    if (!auth.currentUser) return;
    try {
      const response = await authenticatedFetch("/business/extension-whitelist");
      if (response.ok) {
        const data = await response.json();
        setWhitelist(Array.isArray(data) ? data : []);
        setWhitelistLoaded(true);
        setWhitelistError(null);
        setWhitelistLoadError(null);
      } else {
        const data = await response.json().catch(() => ({}));
        const message = errorWithRef(data, () => fallbackError("Could not load your extension allow list.", response));
        setWhitelistError(message);
        setWhitelistLoadError(message);
      }
    } catch (err) {
      console.error("Error fetching extension whitelist:", err);
      const message = err instanceof Error ? err.message : "Could not load your extension allow list.";
      setWhitelistError(message);
      setWhitelistLoadError(message);
    }
  }, []);

  // Admin-only: pending member requests awaiting approve/deny. Its failure is `requestsError`, never
  // `whitelistError` (ENG-3528, above).
  const fetchRequests = useCallback(async () => {
    if (!auth.currentUser) return;
    try {
      const response = await authenticatedFetch("/business/extension-requests");
      if (response.ok) {
        const data = await response.json();
        setRequests(Array.isArray(data) ? data : []);
        setRequestsLoaded(true);
        setRequestsError(null);
      } else {
        const data = await response.json().catch(() => ({}));
        setRequestsError(errorWithRef(data, () => fallbackError("Could not load pending extension requests.", response)));
      }
    } catch (err) {
      console.error("Error fetching extension requests:", err);
      setRequestsError(err instanceof Error ? err.message : "Could not load pending extension requests.");
    }
  }, []);

  /** ENG-3528: Try again after the pending requests failed to load. Shows them loading meanwhile. */
  const retryRequests = useCallback(async () => {
    setRequestsError(null);
    setRequestsLoaded(false);
    await fetchRequests();
  }, [fetchRequests]);

  /**
   * ENG-3350: Try again after the list failed to load. Clears the failure first, so the section shows
   * it loading meanwhile, and reloads the pending requests with it.
   */
  const retryWhitelist = useCallback(async () => {
    setWhitelistLoadError(null);
    setWhitelistError(null);
    setRequestsError(null);
    await Promise.all([fetchWhitelist(), fetchRequests()]);
  }, [fetchWhitelist, fetchRequests]);

  const addExtension = useCallback(async (
    extensionName: string,
    chromeExtensionId: string,
    notes?: string
  ): Promise<{ success: boolean; error?: string }> => {
    if (!extensionName.trim() || !chromeExtensionId.trim()) {
      return { success: false, error: "Name and extension ID are required" };
    }
    setAddingExtension(true);
    setWhitelistError(null);
    try {
      const response = await authenticatedFetch("/business/extension-whitelist", {
        method: "POST",
        body: JSON.stringify({
          extensionName: extensionName.trim(),
          chromeExtensionId: chromeExtensionId.trim(),
          notes: notes?.trim() || null,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        await fetchWhitelist();
        // ENG-2990: allow-list changed — tell the extension now rather than at the next poll.
        notifyOrgPolicyChanged();
        return { success: true };
      }
      // 409 (duplicate) surfaces as data.error/message from the backend.
      const msg = data.error || data.message || "Failed to add extension";
      setWhitelistError(msg);
      return { success: false, error: msg };
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to add extension";
      setWhitelistError(msg);
      return { success: false, error: msg };
    } finally {
      setAddingExtension(false);
    }
  }, [fetchWhitelist]);

  // ENG-3351: a refusal carries the server's reason. The caller has its own words for when there is
  // none, and they claim the entry is still allowed, which a 404 (already gone) would make false.
  const removeExtension = useCallback(async (id: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await authenticatedFetch(`/business/extension-whitelist/${id}`, {
        method: "DELETE",
      });
      if (response.ok) {
        await fetchWhitelist();
        // ENG-2990: allow-list changed — tell the extension now rather than at the next poll.
        notifyOrgPolicyChanged();
        return { success: true };
      }
      const data = await response.json().catch(() => ({}));
      return { success: false, error: data.error || data.message };
    } catch (err) {
      console.error("Error removing extension:", err);
      return { success: false };
    }
  }, [fetchWhitelist]);

  /**
   * ENG-3351: Undo after an allow-list removal. The removal was sent at once, so Undo adds the same
   * entry back: its extension id, name and notes, which is all an entry holds besides its own id and
   * when it was added. The server gives it a new id and it sorts as the newest.
   *
   * Its own call rather than `addExtension`, which drives the add dialog's spinner and banner; a
   * failed Undo is reported where the Undo was offered. A 409 means the id is on the list already,
   * put back by someone else, which is what Undo asked for.
   */
  const restoreExtension = useCallback(async (
    entry: ExtensionWhitelistEntry,
    options: RestoreExtensionOptions = {},
  ): Promise<RestoreExtensionResult> => {
    try {
      // ENG-3502: another admin can empty the list during the Undo window, and putting the entry back
      // into an empty list would start a new list without the first-entry confirmation. So an Undo
      // reads the list fresh first. An empty list is answered, not written to; the caller asks.
      if (options.onlyIntoExistingList) {
        const current = await authenticatedFetch("/business/extension-whitelist");
        // Unreadable: nothing is written, since starting a list unasked is what this guards against.
        // The caller's own failure line sends the admin to Add extension, which asks.
        if (!current.ok) return { success: false };
        const data = await current.json().catch(() => null);
        const list: ExtensionWhitelistEntry[] = Array.isArray(data) ? data : [];
        if (list.length === 0) {
          // Shown as it is now, so the dialog that follows sits over "Haven's defaults", which is true.
          setWhitelist([]);
          return { success: false, listEmpty: true };
        }
      }
      const response = await authenticatedFetch("/business/extension-whitelist", {
        method: "POST",
        body: JSON.stringify({
          extensionName: entry.extensionName,
          chromeExtensionId: entry.chromeExtensionId,
          notes: entry.notes ?? null,
        }),
      });
      if (response.ok || response.status === 409) {
        await fetchWhitelist();
        // ENG-2990: allow-list changed — tell the extension now rather than at the next poll.
        notifyOrgPolicyChanged();
        return { success: true };
      }
      const data = await response.json().catch(() => ({}));
      return { success: false, error: data.error || data.message };
    } catch (err) {
      console.error("Error restoring extension:", err);
      return { success: false };
    }
  }, [fetchWhitelist]);

  // One-click suggested defaults: POST each not-already-present entry. The backend's unique
  // (tenant_id, chrome_extension_id) index 409s on any that slipped in concurrently, which we
  // treat as already-added rather than an error. Returns how many were newly added, and how many
  // could not be: ENG-3350, the caller dropped this and a batch that failed outright said nothing.
  const addSuggested = useCallback(async (items: SuggestedExtension[], notes: string): Promise<{ added: number; failed: number }> => {
    const present = new Set(whitelist.map((e) => e.chromeExtensionId));
    const toAdd = items.filter((item) => !present.has(item.chromeExtensionId));
    // Fire the POSTs concurrently - each is a distinct id, so they can't collide on the unique
    // (tenant_id, chrome_extension_id) index, and the whole batch finishes in ~one round-trip.
    const results = await Promise.all(
      toAdd.map((item) =>
        authenticatedFetch("/business/extension-whitelist", {
          method: "POST",
          body: JSON.stringify({
            extensionName: item.name,
            chromeExtensionId: item.chromeExtensionId,
            notes,
          }),
        })
          .then((response): "added" | "present" | "failed" =>
            response.ok ? "added" : response.status === 409 ? "present" : "failed")
          .catch((err): "failed" => {
            console.error(`Error adding suggested extension ${item.name}:`, err);
            return "failed";
          })
      )
    );
    const added = results.filter((r) => r === "added").length;
    const failed = results.filter((r) => r === "failed").length;
    await fetchWhitelist();
    // ENG-2990: allow-list changed — tell the extension now rather than at the next poll.
    notifyOrgPolicyChanged();
    return { added, failed };
  }, [whitelist, fetchWhitelist]);

  // Member-facing: request an extension. Any active business/premium member may call this;
  // the backend emails the tenant's admins. ENG-3353: the member's own list of requests and their
  // decisions is `useMyExtensionRequests`, read by the member's Extensions page.
  //
  // ENG-3399: a refusal's `code` comes back too (already pending, declined less than a day ago, the
  // day's maximum), so the form can say it as the answer it is rather than as an error.
  const submitRequest = useCallback(async (
    extensionName: string,
    chromeExtensionId: string,
    reason?: string
  ): Promise<SubmitRequestResult> => {
    if (!extensionName.trim() || !chromeExtensionId.trim()) {
      return { success: false, error: "Name and extension ID are required" };
    }
    try {
      const response = await authenticatedFetch("/business/extension-requests", {
        method: "POST",
        body: JSON.stringify({
          extensionName: extensionName.trim(),
          chromeExtensionId: chromeExtensionId.trim(),
          reason: reason?.trim() || null,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) return { success: true };
      return {
        success: false,
        error: data.error || data.message || "Failed to send request",
        ...(typeof data.code === "string" ? { code: data.code } : {}),
      };
    } catch (err: unknown) {
      return { success: false, error: err instanceof Error ? err.message : "Failed to send request" };
    }
  }, []);

  /**
   * Approve a member's request: the extension joins the organization's allow list, for every member
   * (ENG-3353). A denied extension is refused, and the refusal comes back as `denyConflict` so the
   * section can explain it and offer to take it off the deny list — which is this same call with
   * `removeFromDenyList`, sent only once the admin has confirmed.
   *
   * No body unless lifting: that is the request every backend has always accepted.
   */
  const approveRequest = useCallback(async (id: string, options: ApproveRequestOptions = {}): Promise<ApproveRequestResult> => {
    try {
      const response = await authenticatedFetch(`/business/extension-requests/${id}/approve`, {
        method: "POST",
        ...(options.removeFromDenyList ? { body: JSON.stringify({ removeFromDenyList: true }) } : {}),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        await Promise.all([fetchRequests(), fetchWhitelist()]);
        // ENG-2990: allow-list changed — tell the extension now rather than at the next poll.
        notifyOrgPolicyChanged();
        // ENG-3353: and the deny list, when this took the extension off it.
        if (options.removeFromDenyList) notifyDenyListChanged();
        return { success: true };
      }
      const denyConflict = response.status === 409 ? denyConflictFrom(data, "This extension") : null;
      if (denyConflict) {
        // Still pending and nothing written, so the row stays; the section asks what to do.
        return { success: false, error: data.error, denyConflict };
      }
      // 409 if another admin already decided it - refetch so the stale row disappears.
      await fetchRequests();
      return { success: false, error: data.error || data.message || "Failed to approve request" };
    } catch (err: unknown) {
      return { success: false, error: err instanceof Error ? err.message : "Failed to approve request" };
    }
  }, [fetchRequests, fetchWhitelist]);

  const denyRequest = useCallback(async (id: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await authenticatedFetch(`/business/extension-requests/${id}/deny`, {
        method: "POST",
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        await fetchRequests();
        return { success: true };
      }
      await fetchRequests();
      return { success: false, error: data.error || data.message || "Failed to deny request" };
    } catch (err: unknown) {
      return { success: false, error: err instanceof Error ? err.message : "Failed to deny request" };
    }
  }, [fetchRequests]);

  const reset = useCallback(() => {
    setWhitelist([]);
    setRequests([]);
    setWhitelistError(null);
    setWhitelistLoadError(null);
    setWhitelistLoaded(false);
    setRequestsLoaded(false);
    setRequestsError(null);
  }, []);

  /**
   * Memoised for the same reason as useBusinessGroups (ENG-2722): consumers hold the whole object,
   * so a fresh one per render destabilises any effect that depends on it.
   *
   * ⚠ Unlike useBusinessGroups, this object DOES change identity during normal use: `addSuggested`
   * reads `whitelist` to skip entries already present, so it must be recreated when the list is —
   * and the object with it. That is correct, not a leak. It is also why a call site should depend on
   * the SPECIFIC function it calls rather than on this object: an effect keyed on the whole thing
   * would re-run every time the allow-list changed.
   */
  return useMemo(
    () => ({
      whitelist,
      requests,
      whitelistError,
      addingExtension,
      whitelistLoaded,
      whitelistLoadError,
      requestsLoaded,
      requestsError,
      setWhitelistError,
      fetchWhitelist,
      fetchRequests,
      retryWhitelist,
      retryRequests,
      addExtension,
      removeExtension,
      restoreExtension,
      addSuggested,
      submitRequest,
      approveRequest,
      denyRequest,
      reset,
    }),
    [whitelist, requests, whitelistError, addingExtension, whitelistLoaded, whitelistLoadError, requestsLoaded, requestsError, setWhitelistError, fetchWhitelist, fetchRequests, retryWhitelist, retryRequests, addExtension, removeExtension, restoreExtension, addSuggested, submitRequest, approveRequest, denyRequest, reset],
  );
}
